'use client';

import React, { useState } from 'react';
import { Plus, Skull, BarChart2, List } from 'lucide-react';
import MortalitySummaryCards from './MortalitySummaryCards';
import MortalityThresholdAlerts from './MortalityThresholdAlerts';
import MortalityTrendsChart from './MortalityTrendsChart';
import MortalityTable from './MortalityTable';
import MortalityRecordForm from './MortalityRecordForm';
import FlockMortalityStats from './FlockMortalityStats';
import Icon from '@/components/ui/AppIcon';


const TABS = [
  { id: 'records', label: 'Mortality Records', icon: List },
  { id: 'trends', label: 'Trends & Analysis', icon: BarChart2 },
  { id: 'flocks', label: 'Flock Statistics', icon: Skull },
];

export default function MortalityContent() {
  const [activeTab, setActiveTab] = useState('records');
  const [showForm, setShowForm] = useState(false);
  const [savedCount, setSavedCount] = useState(0);
  const [toast, setToast] = useState<string | null>(null);

  function handleSave(data: Record<string, unknown>) {
    setSavedCount(c => c + 1);
    setToast(`Mortality record saved — ${data.flockNumber}. Flock population updated.`);
    setTimeout(() => setToast(null), 4000);
  }

  return (
    <div className="p-6">
      {/* Toast */}
      {toast && (
        <div className="fixed top-4 right-4 z-50 flex items-center gap-3 bg-gray-900 text-white text-sm px-4 py-3 rounded-xl shadow-xl max-w-sm animate-fade-in">
          <span className="w-2 h-2 rounded-full bg-green-400 flex-shrink-0" />
          {toast}
        </div>
      )}

      {/* Page Header */}
      <div className="flex items-start justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Mortality & Culling</h1>
          <p className="text-sm text-gray-500 mt-1">
            Record daily deaths and culled birds, track causes, monitor thresholds, and analyse mortality trends across all flocks.
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition-colors shadow-sm flex-shrink-0"
        >
          <Plus size={16} />
          Record Mortality
        </button>
      </div>

      {/* Summary Cards */}
      <MortalitySummaryCards />

      {/* Threshold Alerts */}
      <MortalityThresholdAlerts />

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-150 ${
                isActive ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={15} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab Content */}
      {activeTab === 'records' && <MortalityTable />}
      {activeTab === 'trends' && <MortalityTrendsChart />}
      {activeTab === 'flocks' && <FlockMortalityStats />}

      {/* Record Form Modal */}
      {showForm && (
        <MortalityRecordForm
          onClose={() => setShowForm(false)}
          onSave={handleSave}
        />
      )}
    </div>
  );
}
